'use strict'

import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext'
import {getSiteEnvVarValue} from '../utils/site-config'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const ALGORITHM = 'AES-GCM'
const GUEST_COOKIE = 'cc-nx-g'
const REGISTERED_COOKIE = 'cc-nx'

/**
 * Converts a hexadecimal string to an ArrayBuffer.
 * @param {string} hex - The hexadecimal string to convert. Must have an even length.
 * @returns {ArrayBuffer} The resulting ArrayBuffer containing the binary data.
 */
function hexToBuffer(hex) {
    const bytes = new Uint8Array(hex.length / 2)
    for (let i = 0; i < bytes.length; i++) {
        bytes[i] = parseInt(hex.substr(i * 2, 2), 16)
    }
    return bytes.buffer
}

/**
 * Converts an ArrayBuffer or TypedArray buffer into a hexadecimal string representation.
 * @param {ArrayBuffer|TypedArray} buffer - The buffer to convert to hex.
 * @returns {string} The hexadecimal string representation of the buffer.
 */
function bufferToHex(buffer) {
    return [...new Uint8Array(buffer)]
        .map(b => b.toString(16).padStart(2, '0'))
        .join('')
}

/**
 * Imports a raw hexadecimal encryption key and returns a CryptoKey object for use with the specified algorithm.
 * @async
 * @param {string} encryptionKey - The raw hexadecimal string representing the encryption key.
 * @returns {Promise<CryptoKey>} A promise that resolves to a CryptoKey object usable for encryption and decryption.
 */
async function getCryptoKey(encryptionKey) {
    return await crypto.subtle.importKey(
        'raw',
        hexToBuffer(encryptionKey),
        {name: ALGORITHM},
        false,
        ['encrypt', 'decrypt']
    )
}

/**
 * Encrypts a payload containing a refresh token and registration status using the provided encryption key.
 *
 * The function serializes the payload, generates a random IV, encrypts the data using the specified algorithm,
 * and returns a string concatenation of the IV, authentication tag, and ciphertext in hexadecimal format.
 *
 * @async
 * @param {string} encryptionKey - The encryption key or key material used for encryption.
 * @param {string} refreshToken - The refresh token to be included in the payload.
 * @param {boolean} isRegistered - Indicates whether the user is registered.
 * @returns {Promise<string>} A promise that resolves to the encrypted payload string in the format: iv.authTag.cipherText (all hex-encoded).
 */
async function encryptPayload(encryptionKey, refreshToken, isRegistered) {
    const key = await getCryptoKey(encryptionKey)
    const dataObj = {
        rt: refreshToken,
        exp: Date.now() + 60 * 1000,
        isRegistered
    }
    const iv = crypto.getRandomValues(new Uint8Array(12))
    const encoded = new TextEncoder().encode(JSON.stringify(dataObj))
    const encryptedBuffer = await crypto.subtle.encrypt(
        { name: ALGORITHM, iv },
        key,
        encoded
    )
    const encryptedBytes = new Uint8Array(encryptedBuffer)
    const cipherText = encryptedBytes.slice(0, -16)
    const authTag = encryptedBytes.slice(-16)

    return (
        bufferToHex(iv) +
        '.' +
        bufferToHex(authTag) +
        '.' +
        bufferToHex(cipherText)
    )
}

/**
 * Decrypts an encrypted payload string using the Web Crypto API and returns the parsed JSON object.
 *
 * The payload is expected to be a dot-separated string containing the IV, authentication tag, and ciphertext in hexadecimal format.
 * The function reconstructs the encrypted data, retrieves the cryptographic key, and attempts to decrypt the payload.
 * If decryption or parsing fails, it logs the error and returns null.
 *
 * @async
 * @param {string} encryptedPayload - The encrypted payload in the format "ivHex.authTagHex.encryptedHex".
 * @returns {Promise<Object|null>} The decrypted and parsed JSON object, or null if decryption fails.
 */
async function decryptPayload(encryptionKey, encryptedPayload) {
    try {
        const [ivHex, authTagHex, encryptedHex] = encryptedPayload.split('.')
        if (!ivHex || !authTagHex || !encryptedHex) return null

        const iv = new Uint8Array(hexToBuffer(ivHex))
        const authTag = new Uint8Array(hexToBuffer(authTagHex))
        const ciphertext = new Uint8Array(hexToBuffer(encryptedHex))
        const combined = new Uint8Array(ciphertext.length + authTag.length)

        combined.set(ciphertext)
        combined.set(authTag, ciphertext.length)

        const key = await getCryptoKey(encryptionKey)
        const decrypted = await crypto.subtle.decrypt(
            {name: ALGORITHM, iv},
            key,
            combined
        )

        return JSON.parse(new TextDecoder().decode(decrypted))
    } catch (e) {
        console.error('Decryption failed:', e)
        return null
    }
}

/**
 * Sets a browser cookie with the specified name and value, expiring in 3 hours.
 * The cookie is set with path '/', SameSite=Lax, and the current hostname as the domain.
 *
 * @param {string} name - The name of the cookie to set.
 * @param {string} value - The value to assign to the cookie.
 */
function setCookie(name, value) {
    const expiresDate = new Date(Date.now() + 3 * 60 * 60 * 1000)
    const cookieString =
        `${name}=${value};` +
        ` expires=${expiresDate.toUTCString()};` +
        ` path=/;` +
        ` SameSite=Lax;`

    document.cookie = cookieString
}

/**
 * Deletes a cookie by name from the current domain.
 * Sets the cookie's expiration date to a past date to ensure removal.
 *
 * @param {string} name - The name of the cookie to delete.
 */
function deleteCookie(name) {
    document.cookie =
        `${name}=; max-age=0; path=/; SameSite=Lax;`
}

/**
 * Custom React hook for handling session sharing logic, including generating secure redirect URLs
 * for session transfer and updating refresh tokens based on encrypted payloads. Utilizes application
 * configuration, current customer, and authentication context to manage session state across domains.
 *
 * @returns {Object} An object containing:
 *   - getRedirectURL(hostname: string): Promise<string|null> - Asynchronously generates a session redirect URL with an encrypted token for the  specified hostname, or null if prerequisites are missing.
 *   - updateRefreshToken(payload: string): Promise<boolean> - Asynchronously decrypts the payload, updates the appropriate refresh token cookie (registered or guest), and refreshes the access token. Returns true if successful, false otherwise.
 */
function useSessionSharing() {
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const encryptionKey = getSiteEnvVarValue(appConfig, 'SESSION_SHARING_ENCRYPTION_KEY', siteId)
    const parameters = {
        ...appConfig.commerceAPI.parameters
    }
    const {data: customer} = useCurrentCustomer()
    const auth = useAuthContext()

    async function getRedirectURL(hostname) {
        if (!hostname || !encryptionKey) return null

        await auth.ready()

        const payload = await encryptPayload(encryptionKey, auth.data.refresh_token, customer?.isRegistered)

        if (!hostname.endsWith('/')) {
            hostname = hostname + '/'
        }

        return `${hostname}session-redirect?token=${payload}`
    }

    async function updateRefreshToken(payload) {
        if (!encryptionKey) return null

        var data = await decryptPayload(encryptionKey, payload)

        if (!data || !data.rt || Date.now() > data.exp) return false

        const registeredCacheName = REGISTERED_COOKIE + '_' + siteId
        const guestCacheName = GUEST_COOKIE + '_' + siteId

        if (data.isRegistered) {
            deleteCookie(guestCacheName)
            setCookie(registeredCacheName, data.rt)
        } else {
            deleteCookie(registeredCacheName)
            setCookie(guestCacheName, data.rt)
        }

        await auth.refreshAccessToken()

        return true
    }

    return {
        getRedirectURL,
        updateRefreshToken
    }
}

export {useSessionSharing}
